import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HttpError } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { t } from '@web/i18n';
import {
  projectsRepository,
  triageRepository,
  type TriageConfigResponse,
  type UpdateTriageConfigInput,
} from '@web/data/repositories';
import { useOrgContext, useSnackbar } from '@web/ui/core';

/** A single squad as offered by the reference panel. */
interface TriageSquadRef {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  /** True when the squad's `code` already appears in the prompt. */
  readonly isUsed: boolean;
}

/** Squads grouped under their parent project for the reference aside. */
interface TriageProjectGroup {
  readonly projectId: string;
  readonly projectName: string;
  readonly squads: readonly TriageSquadRef[];
}

/** A project + its squads as returned by the projects list, pre-coverage. */
interface RawGroup {
  readonly projectId: string;
  readonly projectName: string;
  readonly squads: readonly { id: string; code: string; name: string }[];
}

/** Case-insensitive substring test for whether `code` appears in `prompt`. */
function promptContainsCode(prompt: string, code: string): boolean {
  return prompt.toLowerCase().includes(code.toLowerCase());
}

const errorMessage = (err: unknown): string => {
  if (err instanceof HttpError) return err.message;
  if (err instanceof Error) return err.message;
  return t('triage.toast.error');
};

export function useTriageConfigPage() {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const { data: server } = useQuery({
    queryKey: queryKeys.triage.config(orgId),
    queryFn: () => triageRepository.loadTriageConfig(orgId),
  });

  const { data: projects } = useQuery({
    queryKey: queryKeys.projects.list(orgId),
    queryFn: () => projectsRepository.loadProjects(orgId),
  });

  const [enabled, setEnabled] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [saved, setSaved] = useState<{ enabled: boolean; prompt: string } | null>(null);
  const [insertedCode, setInsertedCode] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const insertedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial hydration from the server query. The mutation's onSuccess
  // handles all later syncs explicitly so user edits never get clobbered
  // mid-flight — mirrors use_org_board_config_page.
  if (server && saved === null) {
    setSaved({ enabled: server.enabled, prompt: server.prompt });
    setEnabled(server.enabled);
    setPrompt(server.prompt);
  }

  const mutation = useMutation({
    mutationFn: (payload: UpdateTriageConfigInput) =>
      triageRepository.updateTriageConfig(orgId, payload),
    onSuccess: async (updated) => {
      queryClient.setQueryData<TriageConfigResponse>(queryKeys.triage.config(orgId), updated);
      setSaved({ enabled: updated.enabled, prompt: updated.prompt });
      setEnabled(updated.enabled);
      setPrompt(updated.prompt);
      await queryClient.invalidateQueries({ queryKey: queryKeys.triage.config(orgId) });
      showSnackbar({ severity: 'success', message: t('triage.toast.saved') });
    },
    onError: async (err) => {
      // Stale-version conflict (409): refresh the cached config so the next
      // save carries the current `_v` instead of the rejected stale one.
      if (err instanceof HttpError && err.status === 409) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.triage.config(orgId) });
      }
      showSnackbar({ severity: 'error', message: errorMessage(err) });
    },
  });

  // "Regerar prompt" — asks the backend LLM to draft a prompt from the org's
  // squads. Only updates the editor draft; the user still Saves to persist.
  const regenerateMutation = useMutation({
    mutationFn: () => triageRepository.regeneratePrompt(orgId),
    onSuccess: (res) => {
      setPrompt(res.prompt);
    },
    onError: (err) => {
      showSnackbar({ severity: 'error', message: errorMessage(err) });
    },
  });

  const rawGroups = useMemo<RawGroup[]>(
    () =>
      (projects ?? []).map((p) => ({
        projectId: p.id,
        projectName: p.name,
        squads: p.squads.map((s) => ({ id: s.id, code: s.code, name: s.name })),
      })),
    [projects],
  );

  const totalSquads = useMemo(
    () => rawGroups.reduce((n, g) => n + g.squads.length, 0),
    [rawGroups],
  );

  const usedCount = useMemo(
    () =>
      rawGroups.reduce(
        (n, g) => n + g.squads.filter((s) => promptContainsCode(prompt, s.code)).length,
        0,
      ),
    [rawGroups, prompt],
  );

  // Projects with at least one squad, decorated with per-squad coverage.
  // Empty projects are dropped from the panel but still counted in total.
  const groups = useMemo<TriageProjectGroup[]>(
    () =>
      rawGroups
        .filter((g) => g.squads.length > 0)
        .map((g) => ({
          projectId: g.projectId,
          projectName: g.projectName,
          squads: g.squads.map((s) => ({
            ...s,
            isUsed: promptContainsCode(prompt, s.code),
          })),
        })),
    [rawGroups, prompt],
  );

  const isDirty = saved !== null && (enabled !== saved.enabled || prompt !== saved.prompt);

  /**
   * Inserts a squad code at the textarea cursor (or end), refocuses, and
   * flashes "inserido" on the chip for ~1.1s. The focus/selection step is
   * the one piece that must touch the DOM imperatively — done in the
   * event handler, not a `useEffect`.
   */
  function insertCode(code: string): void {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? prompt.length;
    const end = el?.selectionEnd ?? prompt.length;
    const next = prompt.slice(0, start) + code + prompt.slice(end);
    setPrompt(next);

    const caret = start + code.length;
    queueMicrotask(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(caret, caret);
    });

    setInsertedCode(code);
    if (insertedTimer.current) clearTimeout(insertedTimer.current);
    insertedTimer.current = setTimeout(() => {
      setInsertedCode(null);
    }, 1100);
  }

  function regenerate(): void {
    if (regenerateMutation.isPending) return;
    regenerateMutation.mutate();
  }

  function discard(): void {
    if (!saved) return;
    setEnabled(saved.enabled);
    setPrompt(saved.prompt);
  }

  function save(): void {
    // Guard re-entrancy: ignore saves while a request is in flight so a
    // double ⌘S / click can't fire two PUTs with the same `expectedV`.
    if (!server || mutation.isPending) return;
    mutation.mutate({ expectedV: server._v, enabled, prompt });
  }

  return {
    isReady: server !== undefined,
    enabled,
    setEnabled,
    prompt,
    setPrompt,
    groups,
    totalSquads,
    usedCount,
    insertedCode,
    textareaRef,
    insertCode,
    regenerate,
    isRegenerating: regenerateMutation.isPending,
    isDirty,
    isSaving: mutation.isPending,
    discard,
    save,
  };
}
